import React, { useEffect, useMemo, useState } from 'react';
import { Mail, MessageCircle, AlertTriangle, ChevronDown, Calendar, Loader2, Phone } from 'lucide-react';
import { adminApi } from './api';

const STATUS_LABELS = {
  new: 'не ответила',
  in_progress: 'в работе',
  answered: 'ответила',
  declined: 'не подошёл',
};

function greetingByHour(date) {
  const h = date.getHours();
  if (h < 5) return 'Доброй ночи';
  if (h < 12) return 'Доброе утро';
  if (h < 18) return 'Добрый день';
  return 'Добрый вечер';
}

function formatDateRu(date) {
  return new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
}

function formatTime(iso) {
  try {
    return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch { return ''; }
}

function ReceptionToggle({ value, onChange, disabled }) {
  const on = value === 'open';
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="dark-eyebrow">Сейчас</div>
        <div className="dark-h2 mt-1" data-testid="reception-label">
          {on ? 'Принимаю' : 'В отпуске'}
        </div>
        <div className="dark-meta mt-1">
          {on ? 'Аня предлагает записаться' : 'Аня говорит, что вы вернётесь позже'}
        </div>
      </div>
      <button
        onClick={() => !disabled && onChange(on ? 'vacation' : 'open')}
        disabled={disabled}
        className={`dark-toggle ${on ? 'on' : ''}`}
        aria-pressed={on}
        data-testid="reception-toggle"
        style={{ opacity: disabled ? 0.5 : 1 }}
      >
        <span className="knob" />
      </button>
    </div>
  );
}

function CountRing({ percent, big, label, color = 'gold' }) {
  const R = 50;
  const C = 2 * Math.PI * R;
  const offset = C - (Math.min(100, Math.max(0, percent)) / 100) * C;
  return (
    <div style={{ position: 'relative', display: 'inline-grid', placeItems: 'center' }}>
      <svg width="124" height="124" viewBox="0 0 124 124">
        <circle cx="62" cy="62" r={R} className="ring-bg" />
        <circle cx="62" cy="62" r={R}
          className={`ring-fill ${color === 'wine' ? 'wine' : ''}`}
          strokeDasharray={C}
          strokeDashoffset={offset}
          transform="rotate(-90 62 62)"
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{
          fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic',
          fontSize: 46, color: 'var(--cream)', lineHeight: 1,
        }}>{big}</span>
        <span className="dark-meta" style={{ marginTop: 6 }}>{label}</span>
      </div>
    </div>
  );
}

function CrisisCard({ event }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="dark-card wine p-7" style={{ padding: 28 }} data-testid="crisis-card">
      <div className="flex items-start gap-4">
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'rgba(210, 34, 99, 0.20)', display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <AlertTriangle size={20} style={{ color: 'var(--wine-bright)' }} strokeWidth={1.8} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'var(--wine-bright)', fontWeight: 600,
          }}>
            Срочно обратить внимание
          </div>
          <h3 className="dark-h2" style={{ fontSize: 26, marginTop: 6 }}>
            {event.kind === 'chat' ? 'У Ани был непростой разговор' : 'Кризисный запрос в заявке'}
          </h3>
          <p style={{ color: 'rgba(252, 252, 244, 0.85)', fontSize: 14, marginTop: 10, lineHeight: 1.6 }}>
            {((event.kind === 'chat' ? event.summary : event.message) || '').slice(0, 220)}
            {((event.kind === 'chat' ? event.summary : event.message) || '').length > 220 && '…'}
          </p>
          <button
            onClick={() => setOpen(!open)}
            style={{
              marginTop: 14, fontSize: 12, color: 'var(--gold)',
              display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none',
            }}
          >
            {open ? 'Свернуть' : 'Прочитать полностью'}
            <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
          </button>
          {open && (
            <div style={{
              marginTop: 16, paddingTop: 16,
              borderTop: '1px solid rgba(252, 252, 244, 0.10)',
            }}>
              <pre style={{
                fontFamily: 'Nunito, sans-serif', fontSize: 14,
                color: 'rgba(252, 252, 244, 0.88)', whiteSpace: 'pre-wrap', lineHeight: 1.6, margin: 0,
              }}>
                {event.kind === 'chat' ? event.summary : event.message}
              </pre>
              {event.kind === 'contact' && event.contact && (
                <p style={{ marginTop: 14, fontSize: 14, color: 'var(--gold)' }}>
                  Контакт: <span style={{ color: 'var(--cream)' }}>{event.contact}</span>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactCard({ event, onStatus }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="dark-card hoverable" style={{ padding: 22 }} data-testid={`contact-card-${event.id}`}>
      <div className="flex items-start gap-4">
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(197, 165, 114, 0.12)', display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <Mail size={16} style={{ color: 'var(--gold)' }} strokeWidth={1.8} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex flex-wrap items-baseline" style={{ gap: 8 }}>
            <h4 className="dark-h2" style={{ fontSize: 20 }}>
              Вам написала {event.name}
            </h4>
            <span className="dark-meta">{formatTime(event.ts)}</span>
          </div>
          <p style={{ fontSize: 14, color: 'rgba(252, 252, 244, 0.78)', marginTop: 6, lineHeight: 1.6 }}>
            {(event.message || '(без сообщения)').slice(0, 140)}
            {(event.message || '').length > 140 && '…'}
          </p>

          <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 12 }}>
            <span className={`dark-tag ${event.status === 'new' ? 'new' : event.status === 'answered' ? 'answered' : ''}`}>
              {STATUS_LABELS[event.status] || event.status}
            </span>
            {event.preferred_time && (
              <span className="dark-tag">Удобно: {event.preferred_time}</span>
            )}
            <button
              onClick={() => setOpen(!open)}
              style={{
                marginLeft: 'auto', fontSize: 12, color: 'var(--cream-mute)',
                background: 'transparent', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              {open ? 'Свернуть' : 'Подробнее'}
              <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
            </button>
          </div>

          {open && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ink-line)' }}>
              <div style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--cream)' }}>
                <Phone size={14} style={{ color: 'var(--cream-soft)' }} />
                <a href={`tel:${event.contact.replace(/\s+/g, '')}`}
                   style={{ color: 'inherit' }}>{event.contact}</a>
              </div>
              {event.message && (
                <pre style={{
                  fontFamily: 'Nunito, sans-serif', fontSize: 14, color: 'rgba(252, 252, 244, 0.85)',
                  whiteSpace: 'pre-wrap', lineHeight: 1.6, marginTop: 12,
                  background: 'rgba(255,255,255,0.03)', padding: 14, borderRadius: 10,
                  border: '1px solid var(--ink-line)',
                }}>
                  {event.message}
                </pre>
              )}
              <div className="flex flex-wrap gap-2" style={{ marginTop: 14 }}>
                {event.status !== 'in_progress' && (
                  <button
                    onClick={() => onStatus(event.id, 'in_progress')}
                    className="dark-btn dark-btn-ghost"
                    style={{ padding: '8px 14px', fontSize: 12 }}
                    data-testid={`mark-in-progress-${event.id}`}
                  >Взять в работу</button>
                )}
                {event.status !== 'answered' && (
                  <button
                    onClick={() => onStatus(event.id, 'answered')}
                    className="dark-btn dark-btn-primary"
                    style={{ padding: '8px 14px', fontSize: 12 }}
                    data-testid={`mark-answered-${event.id}`}
                  >Ответила</button>
                )}
                {event.status !== 'declined' && (
                  <button
                    onClick={() => onStatus(event.id, 'declined')}
                    className="dark-btn dark-btn-ghost"
                    style={{ padding: '8px 14px', fontSize: 12 }}
                    data-testid={`mark-declined-${event.id}`}
                  >Не подошёл</button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatCard({ event }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="dark-card hoverable" style={{ padding: 22 }} data-testid={`chat-card-${event.id}`}>
      <div className="flex items-start gap-4">
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(148, 0, 74, 0.18)', display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <MessageCircle size={16} style={{ color: 'var(--wine-bright)' }} strokeWidth={1.8} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex flex-wrap items-baseline" style={{ gap: 8 }}>
            <h4 className="dark-h2" style={{ fontSize: 20 }}>
              Аня поговорила с гостьей — {event.duration_min} мин
            </h4>
            <span className="dark-meta">{formatTime(event.ts)}</span>
          </div>
          <p style={{ fontSize: 14, color: 'rgba(252, 252, 244, 0.78)', marginTop: 6, lineHeight: 1.6 }}>
            {(event.summary || '').split('\n').slice(0, 2).join(' · ').slice(0, 180)}
            {(event.summary || '').length > 180 && '…'}
          </p>
          <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 12 }}>
            <span className="dark-tag">{event.message_count} сообщений</span>
            <button
              onClick={() => setOpen(!open)}
              style={{
                marginLeft: 'auto', fontSize: 12, color: 'var(--cream-mute)',
                background: 'transparent', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
              data-testid={`chat-expand-${event.id}`}
            >
              {open ? 'Свернуть' : 'Прочитать сводку'}
              <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
            </button>
          </div>

          {open && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ink-line)' }}>
              <div className="dark-eyebrow" style={{ marginBottom: 8 }}>Сводка</div>
              <pre style={{
                fontFamily: 'Nunito, sans-serif', fontSize: 14, color: 'rgba(252, 252, 244, 0.88)',
                whiteSpace: 'pre-wrap', lineHeight: 1.6,
                background: 'rgba(255,255,255,0.03)', padding: 14, borderRadius: 10,
                border: '1px solid var(--ink-line)', margin: 0,
              }}>
                {event.summary}
              </pre>
              {event.transcript && (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--cream-soft)' }}>
                    Полный разговор
                  </summary>
                  <pre style={{
                    marginTop: 8, fontFamily: 'Nunito, sans-serif', fontSize: 12,
                    color: 'rgba(252, 252, 244, 0.75)', whiteSpace: 'pre-wrap', lineHeight: 1.6,
                    background: 'rgba(0,0,0,0.18)', padding: 14, borderRadius: 10,
                    border: '1px solid var(--ink-line)', maxHeight: 360, overflow: 'auto',
                  }}>
                    {event.transcript}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminToday() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingReception, setSavingReception] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const d = await adminApi.today();
      setData(d);
      setError('');
    } catch (err) {
      setError(err.message || 'Не получилось загрузить');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const tick = () => { load(); };
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReception = async (val) => {
    setSavingReception(true);
    try {
      await adminApi.setReception(val);
      setData((d) => ({ ...d, reception: val }));
    } catch {/* ignore */} finally {
      setSavingReception(false);
    }
  };

  const onUpdateStatus = async (id, status) => {
    try {
      await adminApi.updateRequest(id, status);
      load();
    } catch {/* ignore */}
  };

  const now = useMemo(() => new Date(), []);

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: '120px 0' }} data-testid="admin-loading">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--cream-soft)' }} />
      </div>
    );
  }

  const events = data?.events || [];
  const counts = data?.counts || { total: 0, contacts: 0, chats: 0, crisis: 0, new_contacts: 0 };
  const crisisEvent = events.find((e) => e.crisis);
  const contactEvents = events.filter((e) => e.kind === 'contact');
  const chatEvents = events.filter((e) => e.kind === 'chat');
  const gaugePercent = Math.min(100, (counts.total / 10) * 100);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} data-testid="admin-today">
      {/* Шапка */}
      <div className="dark-card" style={{ padding: 32 }}>
        <div className="flex flex-col lg:flex-row gap-8">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="dark-eyebrow" data-testid="admin-date">{formatDateRu(now)}</div>
            <h1 className="dark-h1" style={{ marginTop: 8 }} data-testid="admin-greeting">
              {greetingByHour(now)},<br/>Светлана
            </h1>
            <div className="flex flex-wrap gap-2" style={{ marginTop: 22 }} data-testid="admin-counts">
              <span className="dark-pill wine">
                <span className="dot" />
                {counts.contacts === 0 ? 'Заявок пока нет' :
                  counts.contacts === 1 ? '1 новая заявка' : `${counts.contacts} новых заявок`}
              </span>
              <span className="dark-pill gold">
                <span className="dot" />
                {counts.chats === 0 ? 'Аня сегодня молчит' :
                  counts.chats === 1 ? 'Аня поговорила с 1 гостьей' :
                  `Аня поговорила с ${counts.chats} гостьями`}
              </span>
              {counts.crisis > 0 && (
                <span className="dark-pill warn">
                  <AlertTriangle size={12} />Кризисный кейс
                </span>
              )}
              <span className="dark-pill">
                <span className="live-dot" />
                обновляется автоматически
              </span>
            </div>
          </div>

          <div className="flex flex-col items-center gap-6" style={{ minWidth: 260 }}>
            <CountRing
              percent={gaugePercent}
              big={counts.total}
              label={counts.total === 1 ? 'событие сегодня' : 'событий сегодня'}
              color={counts.crisis > 0 ? 'wine' : 'gold'}
            />
            <div style={{ width: '100%', borderTop: '1px solid var(--ink-line)', paddingTop: 18 }}>
              <ReceptionToggle
                value={data?.reception || 'open'}
                onChange={onReception}
                disabled={savingReception}
              />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="dark-card" style={{ padding: 18, color: 'var(--wine-bright)' }} data-testid="admin-error">
          {error}
        </div>
      )}

      {crisisEvent && <CrisisCard event={crisisEvent} />}

      {contactEvents.length > 0 && (
        <section data-testid="contacts-section">
          <h2 className="dark-h2" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <Mail size={16} style={{ color: 'var(--gold)' }} strokeWidth={1.6} />
            Заявки на сегодня
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {contactEvents.map((e) => <ContactCard key={e.id} event={e} onStatus={onUpdateStatus} />)}
          </div>
        </section>
      )}

      {chatEvents.length > 0 && (
        <section data-testid="chats-section">
          <h2 className="dark-h2" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <MessageCircle size={16} style={{ color: 'var(--wine-bright)' }} strokeWidth={1.6} />
            Разговоры с Аней
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {chatEvents.map((e) => <ChatCard key={e.id} event={e} />)}
          </div>
        </section>
      )}

      {events.length === 0 && (
        <div className="dark-card" style={{ padding: 60, textAlign: 'center' }} data-testid="admin-empty">
          <Calendar size={28} style={{ color: 'var(--cream-soft)', margin: '0 auto 16px', opacity: 0.6 }} strokeWidth={1.4} />
          <p className="dark-h2" style={{ fontSize: 26 }}>Сегодня день для себя</p>
          <p style={{ fontSize: 14, color: 'var(--cream-soft)', marginTop: 12, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
            Никто пока не писал — это тоже хорошо. Я тихо подскажу, как только кто-то придёт к Ане или оставит заявку.
          </p>
        </div>
      )}
    </div>
  );
}
